/**
 * Civilian Nights Out — create page (flyer-first UX).
 *
 * Flow:
 *   1. User snaps / uploads a club poster / IG flyer.
 *   2. Backend's /extract-flyer-public hits Gemini Vision, returns
 *      name + date + time + address + description (+ uploads the
 *      flyer image to GCS).
 *   3. Form auto-fills. User skims, tweaks anything the AI missed,
 *      hits submit. (Manual entry stays available — collapsed by
 *      default but always reachable if the AI struggles.)
 *
 * Anonymous-friendly — no login required. We stash the returned
 * edit_token in localStorage so the creator's device can edit/delete
 * later from the same browser.
 */
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Loader2, Sparkles, Share2, ArrowLeft, MapPin, Calendar as CalIcon,
  Upload, Wand2, ImageIcon, RotateCcw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOCAL_KEY = 'civilian_edit_tokens';

const stashEditToken = (civId, token) => {
  try {
    const cur = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
    cur[civId] = token;
    localStorage.setItem(LOCAL_KEY, JSON.stringify(cur));
  } catch {
    /* localStorage disabled — non-fatal */
  }
};

const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = () => rej(new Error('Could not read file'));
  r.readAsDataURL(file);
});

const CivilianNightCreatePage = () => {
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [name, setName] = useState('');
  const [creatorName, setCreatorName] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('rollm8s_user') || 'null');
      if (u && (u.name || u.username)) return u.name || u.username;
    } catch {/* ignore */}
    return '';
  });
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [address, setAddress] = useState('');
  const [description, setDescription] = useState('');
  const [ticketInfo, setTicketInfo] = useState('');
  const [ticketUrl, setTicketUrl] = useState('');
  // `external` → ticketed event (Eventbrite/Ticket Source/etc) — each
  // attendee marks their own "Got Ticket" status.
  // `organiser` → creator collects payment directly (games night, BBQ,
  // dinner party) — only the creator can mark attendees as Paid.
  const [paymentMode, setPaymentMode] = useState('external');
  const [imageUrl, setImageUrl] = useState('');
  const [imageUrlBack, setImageUrlBack] = useState('');
  const [uploadingBack, setUploadingBack] = useState(false);
  const backFileRef = useRef(null);

  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(false);  // toggles UI to "review" mode
  const [submitting, setSubmitting] = useState(false);

  const pickFile = () => fileRef.current?.click();
  const pickBackFile = () => backFileRef.current?.click();

  const uploadBack = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image must be under 15 MB');
      return;
    }
    setUploadingBack(true);
    try {
      const b64 = await fileToBase64(file);
      // Reuse the public extractor purely for the GCS upload side-effect —
      // we ignore the extracted fields for the back side, just keep flyer_url.
      const { data } = await axios.post(
        `${API}/events/extract-flyer-public`,
        { file_data: b64, filename: file.name || 'flyer-back.jpg' },
      );
      if (data.flyer_url) {
        setImageUrlBack(data.flyer_url);
        toast.success('Back of flyer added');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not upload back of flyer');
    } finally {
      setUploadingBack(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Image must be under 15 MB');
      return;
    }
    setExtracting(true);
    try {
      const b64 = await fileToBase64(file);
      const { data } = await axios.post(
        `${API}/events/extract-flyer-public`,
        { file_data: b64, filename: file.name || 'flyer.jpg' },
      );
      const ex = data.extracted || {};
      // Helpful when debugging — leave in until the user reports the
      // autofill is solid (mirrors what the normal-event flow does).
      console.log('[Civilian flyer extract] returned:', ex);

      if (ex.name) setName(ex.name);

      // ── Date normalisation ──────────────────────────────────────
      // <input type="date"> only accepts strict YYYY-MM-DD. Gemini
      // usually returns that format directly, so we fast-path it (also
      // avoids the UTC→local off-by-one that `new Date("YYYY-MM-DD")`
      // can trigger in negative timezones). Falls back to `new Date()`
      // for any other shape Gemini might emit.
      const toIsoDate = (raw) => {
        if (!raw) return null;
        const trimmed = String(raw).trim();
        const fast = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (fast) return `${fast[1]}-${fast[2]}-${fast[3]}`;
        const d = new Date(trimmed);
        if (Number.isNaN(d.getTime())) return null;
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
      };

      // Description-text fallback for the rare case Gemini returns
      // null date but the date is mentioned in the blurb.
      const MONTHS = { jan:0,january:0, feb:1,february:1, mar:2,march:2, apr:3,april:3,
        may:4, jun:5,june:5, jul:6,july:6, aug:7,august:7, sep:8,sept:8,september:8,
        oct:9,october:9, nov:10,november:10, dec:11,december:11 };
      const fromText = (txt) => {
        if (!txt) return null;
        const re = /\b(?:(\d{1,2})(?:st|nd|rd|th)?\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*|(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\s+(\d{1,2})(?:st|nd|rd|th)?)(?:[,\s]+(\d{4}))?/i;
        const m = txt.match(re);
        if (!m) return null;
        const day = parseInt(m[1] || m[4], 10);
        const monKey = (m[2] || m[3] || '').toLowerCase();
        const mon = MONTHS[monKey];
        if (mon === undefined || !day) return null;
        let year = m[5] ? parseInt(m[5], 10) : new Date().getFullYear();
        const candidate = new Date(year, mon, day);
        if (!m[5] && candidate < new Date()) year += 1;
        return `${year}-${String(mon + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      };

      const isoDate = toIsoDate(ex.date) || fromText(ex.description) || fromText(ex.name);
      console.log('[Civilian flyer extract] resolved date →', isoDate, 'from raw:', ex.date);
      if (isoDate) setDate(isoDate);

      if (ex.time) {
        setTime(ex.time);
      } else if (ex.description) {
        // Fallback: scan the description for a time pattern Gemini missed.
        // Matches `22:00`, `10pm`, `10:30 PM`, `21h00`, optional am/pm.
        const m = ex.description.match(/\b([01]?\d|2[0-3])[:h]([0-5]\d)\b|\b(1[0-2]|0?[1-9])\s*(?:[:.]\s*([0-5]\d))?\s*(am|pm)\b/i);
        if (m) {
          let h, mins;
          if (m[1]) { h = parseInt(m[1], 10); mins = m[2]; }
          else {
            h = parseInt(m[3], 10);
            mins = m[4] || '00';
            if (/pm/i.test(m[5]) && h < 12) h += 12;
            if (/am/i.test(m[5]) && h === 12) h = 0;
          }
          setTime(`${String(h).padStart(2, '0')}:${mins}`);
        }
      }
      if (ex.address) setAddress(ex.address);
      if (ex.description) setDescription(ex.description);
      if (data.flyer_url) setImageUrl(data.flyer_url);
      // Extract a buy-tickets URL from the description if the flyer mentions one
      if (ex.description) {
        const urlMatch = ex.description.match(/https?:\/\/[^\s)]+/);
        if (urlMatch && !ticketUrl) setTicketUrl(urlMatch[0]);
      }
      setExtracted(true);
      const filled = [ex.name, ex.date, ex.address].filter(Boolean).length;
      if (filled >= 2) {
        toast.success('Flyer read! Check the details below and tweak anything off.');
      } else {
        toast.warning("AI couldn't read much from that image. Edit the fields below.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Flyer read failed — try a clearer photo or fill in manually.');
      // Still let them fill manually
      setExtracted(true);
    } finally {
      setExtracting(false);
    }
  };

  const resetForm = () => {
    setName(''); setDate(''); setTime(''); setAddress('');
    setDescription(''); setTicketInfo(''); setTicketUrl(''); setImageUrl(''); setImageUrlBack('');
    setExtracted(false);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!name || !date || !address || !creatorName) {
      toast.error('Please fill in event name, your name, date and location');
      return;
    }
    setSubmitting(true);
    try {
      const isoDate = time
        ? new Date(`${date}T${time}:00`).toISOString()
        : new Date(`${date}T20:00:00`).toISOString();
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.post(
        `${API}/civilian-nights`,
        {
          name,
          date: isoDate,
          location: { address },
          description: description || null,
          image_url: imageUrl || null,
          image_url_back: imageUrlBack || null,
          ticket_info: ticketInfo || null,
          ticket_url: ticketUrl || null,
          payment_mode: paymentMode,
          creator_name: creatorName,
        },
        { headers },
      );
      stashEditToken(data.civ_id, data.edit_token);
      toast.success('Created! Share the link in your group chat.');
      navigate(`/civilian/${data.civ_id}?just_created=1`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create event');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="civilian-create-page">
      <Navbar />
      <div className="max-w-xl mx-auto px-4 pt-24">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3" data-testid="civilian-create-back">
          <ArrowLeft className="w-4 h-4 mr-1" />Back
        </Button>

        <div className="mb-5 space-y-1">
          <h1 className="font-heading text-3xl flex items-center gap-2">
            <Sparkles className="w-7 h-7 text-fuchsia-400" />
            Civilian Night Out
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Link-only — never public. Raves, club nights, day parties, anything
            that isn&apos;t a sporting motive.
          </p>
          <ul className="text-sm text-muted-foreground mt-2 space-y-1.5 list-disc pl-5 marker:text-fuchsia-400">
            <li>Snap the flyer to autofill the details</li>
            <li>Share the link with your crew</li>
            <li>They RSVP with one tap</li>
            <li>Everyone sees who&apos;s coming on the shared calendar</li>
          </ul>
        </div>

        {/* Flyer-first upload card */}
        {!extracted && (
          <Card
            className="bg-card/60 border-border/50 border-dashed border-2 mb-4 cursor-pointer hover:border-fuchsia-700/60 transition"
            onClick={pickFile}
            data-testid="civilian-flyer-dropzone"
          >
            <CardContent className="p-8 text-center space-y-3">
              {extracting ? (
                <>
                  <Loader2 className="w-12 h-12 mx-auto text-fuchsia-400 animate-spin" />
                  <p className="font-semibold">Reading the flyer...</p>
                  <p className="text-xs text-muted-foreground">Gemini Vision is pulling the date, location and description.</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-fuchsia-950/60 flex items-center justify-center">
                    <Wand2 className="w-8 h-8 text-fuchsia-300" />
                  </div>
                  <p className="font-semibold text-base">Tap to snap or upload the flyer</p>
                  <p className="text-xs text-muted-foreground">
                    We read it with AI and pre-fill the details for you.
                  </p>
                  <div className="pt-3">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setExtracted(true); }}
                      className="px-4 py-2 rounded-md border border-fuchsia-700/60 text-fuchsia-300 hover:bg-fuchsia-950/40 text-sm font-medium transition"
                      data-testid="civilian-skip-ai"
                    >
                      Or fill in manually
                    </button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onFile}
          data-testid="civilian-flyer-file-input"
        />
        <input
          ref={backFileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={uploadBack}
          data-testid="civilian-flyer-back-file-input"
        />

        {/* Form — appears after the AI extract OR if they skipped the AI step */}
        {extracted && (
          <Card className="bg-card/60 border-border/50">
            <CardContent className="p-5">
              <form onSubmit={submit} className="space-y-4">
                {imageUrl && (
                  <div className="space-y-3">
                    <div className="relative bg-black/40 rounded">
                      <img
                        src={imageUrl}
                        alt="Flyer front"
                        className="w-full max-h-[480px] object-contain rounded"
                        data-testid="civilian-flyer-preview"
                      />
                      <button
                        type="button"
                        onClick={pickFile}
                        className="absolute top-2 right-2 bg-black/70 backdrop-blur text-white text-xs px-3 py-1.5 rounded flex items-center gap-1"
                        data-testid="civilian-replace-flyer"
                      >
                        <ImageIcon className="w-3 h-3" />Replace
                      </button>
                      <span className="absolute top-2 left-2 bg-black/70 backdrop-blur text-white text-[10px] px-2 py-1 rounded uppercase tracking-wide">Front</span>
                    </div>
                    {imageUrlBack ? (
                      <div className="relative bg-black/40 rounded">
                        <img
                          src={imageUrlBack}
                          alt="Flyer back"
                          className="w-full max-h-[480px] object-contain rounded"
                          data-testid="civilian-flyer-back-preview"
                        />
                        <button
                          type="button"
                          onClick={pickBackFile}
                          className="absolute top-2 right-2 bg-black/70 backdrop-blur text-white text-xs px-3 py-1.5 rounded flex items-center gap-1"
                          data-testid="civilian-replace-back"
                        >
                          <ImageIcon className="w-3 h-3" />Replace
                        </button>
                        <span className="absolute top-2 left-2 bg-black/70 backdrop-blur text-white text-[10px] px-2 py-1 rounded uppercase tracking-wide">Back</span>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={pickBackFile}
                        disabled={uploadingBack}
                        className="w-full border-dashed"
                        data-testid="civilian-add-back-btn"
                      >
                        {uploadingBack ? (
                          <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-1" />
                        )}
                        Add back of flyer (optional)
                      </Button>
                    )}
                  </div>
                )}
                {!imageUrl && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={pickFile}
                    className="w-full"
                    data-testid="civilian-upload-flyer-from-form"
                  >
                    <Upload className="w-4 h-4 mr-1" />Add a flyer (optional)
                  </Button>
                )}

                <div>
                  <Label htmlFor="civ-name">Event name *</Label>
                  <Input
                    id="civ-name"
                    data-testid="civilian-name-input"
                    placeholder="e.g. Saturday Rave at Fabric"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="civ-creator">Your name *</Label>
                  <Input
                    id="civ-creator"
                    data-testid="civilian-creator-input"
                    placeholder="What should the group see as the organiser?"
                    value={creatorName}
                    onChange={(e) => setCreatorName(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown next to the event so the group knows who proposed it.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="civ-date" className="flex items-center gap-1.5"><CalIcon className="w-3.5 h-3.5" />Date *</Label>
                    <Input
                      id="civ-date"
                      data-testid="civilian-date-input"
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="civ-time">Time</Label>
                    <Input
                      id="civ-time"
                      data-testid="civilian-time-input"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      placeholder="20:00"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="civ-loc" className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" />Location *</Label>
                  <Input
                    id="civ-loc"
                    data-testid="civilian-location-input"
                    placeholder="Venue, area or postcode"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="civ-desc">Description</Label>
                  <Textarea
                    id="civ-desc"
                    data-testid="civilian-desc-input"
                    rows={3}
                    placeholder="Dress code, DJs, who else is going... whatever the group needs to know."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div>
                  <Label htmlFor="civ-tickets">Tickets info (free text)</Label>
                  <Input
                    id="civ-tickets"
                    data-testid="civilian-tickets-input"
                    placeholder='e.g. "£25 cash at door" or "DM @marc for guestlist"'
                    value={ticketInfo}
                    onChange={(e) => setTicketInfo(e.target.value)}
                  />
                </div>

                {/* Payment-mode toggle — drives the "Got Ticket" vs "Paid"
                    chip behaviour on the detail page. */}
                <div className="bg-card/40 border border-border/50 rounded-lg p-3">
                  <Label className="text-sm font-semibold mb-1 block">
                    Are you collecting payments for this event?
                  </Label>
                  <p className="text-xs text-muted-foreground mb-2.5">
                    Pick &ldquo;Yes&rdquo; for games nights, dinners, BBQs etc.
                    where guests pay you directly. Pick &ldquo;No&rdquo; for
                    ticketed events (Eventbrite, Ticket Source, etc.).
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentMode('organiser')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-all ${
                        paymentMode === 'organiser'
                          ? 'bg-fuchsia-500/20 border-fuchsia-500/60 text-fuchsia-200'
                          : 'bg-transparent border-border/60 text-muted-foreground hover:bg-card/60'
                      }`}
                      data-testid="payment-mode-organiser"
                    >
                      Yes — I&rsquo;ll collect
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentMode('external')}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-semibold border transition-all ${
                        paymentMode === 'external'
                          ? 'bg-fuchsia-500/20 border-fuchsia-500/60 text-fuchsia-200'
                          : 'bg-transparent border-border/60 text-muted-foreground hover:bg-card/60'
                      }`}
                      data-testid="payment-mode-external"
                    >
                      No — external tickets
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="civ-ticket-url">Buy tickets link</Label>
                  <Input
                    id="civ-ticket-url"
                    data-testid="civilian-ticket-url-input"
                    type="url"
                    inputMode="url"
                    placeholder="https://ra.co/events/..."
                    value={ticketUrl}
                    onChange={(e) => setTicketUrl(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown as a tappable button on the event page.
                  </p>
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={resetForm}
                    className="flex-shrink-0"
                    data-testid="civilian-reset-btn"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
                    data-testid="civilian-submit-btn"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Share2 className="w-4 h-4 mr-1" />}
                    Create & get share link
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CivilianNightCreatePage;
